<template>
  <div>
    <b-row>
      <b-col
        md="8"
        class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
      >
        <p class="h5 mt-50 mr-75 ml-50">
          일별/누적
        </p>
        <b-form-radio-group
          v-model="query.isPrefixSum"
          @change="changePrefixSum"
        >
          <b-form-radio :value="false">
            일별 (기간:최대30일)
          </b-form-radio>
          <b-form-radio :value="true">
            누적 (기간:최대1년)
          </b-form-radio>
        </b-form-radio-group>
        <b-form-datepicker
          v-model="query.startDate"
          class="el-def"
          placeholder="조회기간(Fr)"
          style="width: 240px"
        />
        <label class="ml-50 mr-50">~</label>
        <b-form-datepicker
          v-model="query.endDate"
          class="el-def"
          placeholder="조회기간(To)"
          style="width: 240px"
        />
        <label class="ml-50">(*당일데이터 조회 불가)</label>
      </b-col>
      <b-col
        md="4"
        class="d-flex align-items-center justify-content-end"
      >
        <b-button
          variant="primary"
          class="btn-search"
          @click.prevent="fetchData()"
        >
          조회
        </b-button>
        <b-button
          variant="outline-dark"
          class="btn-search ml-1"
          @click.prevent="excelDownload()"
        >
          <feather-icon
            icon="DownloadIcon"
            class="mr-50"
          />
          <span>엑셀 다운로드</span>
        </b-button>
      </b-col>
    </b-row>
    <b-row class="mt-2">
      <b-col
        cols="12"
        class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
      >
        <b-table-simple
          bordered
          responsive
          sticky-header
          style="max-height: 65vh;"
        >
          <colgroup>
            <col style="width: 140px">
            <template v-for="region in headers">
              <col
                :key="`col-total-${region.regionCode}`"
                style="width: 100px"
              >
              <col
                :key="`col-group-${region.regionCode}`"
                style="width: 105px"
              >
            </template>
          </colgroup>
          <b-thead head-variant="light">
            <b-tr>
              <b-th rowspan="2">
                구분
              </b-th>
              <b-th
                v-for="region in headers"
                :key="`header-${region.regionCode}`"
                colspan="2"
              >
                {{ region.regionName }}
              </b-th>
            </b-tr>
            <b-tr>
              <template v-for="region in headers">
                <b-th :key="`sub-total-${region.regionCode}`">
                  유입 수
                </b-th>
                <b-th :key="`sub-group-${region.regionCode}`">
                  유입자 수
                </b-th>
              </template>
            </b-tr>
          </b-thead>
          <b-tbody v-if="data.length > 0">
            <b-tr
              v-for="(m, idx) in data"
              :key="`tr-${idx}`"
            >
              <b-td>{{ m.date }}</b-td>
              <template v-for="region in m.regions">
                <b-td :key="`td-total-${region.regionCode}`">
                  {{ region.entryTotalCount | $numberFormatter }}
                </b-td>
                <b-td :key="`td-group-${region.regionCode}`">
                  {{ region.entryGroupCount | $numberFormatter }}
                </b-td>
              </template>
            </b-tr>
          </b-tbody>

          <b-tbody v-else>
            <b-tr>
              <b-td>
                <div
                  role="alert"
                  aria-live="polite"
                >
                  <div class="text-center my-2">
                    일치하는 데이터가 없습니다.
                  </div>
                </div>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import _ from 'lodash'
import dayjs from 'dayjs'
import axios from '@axios'
import {
  BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue'
import { onMounted, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ExcelJS from "exceljs"
import { saveAs } from "file-saver"

export default {
  components: {
    BTableSimple,
    BThead,
    BTbody,
    BTr,
    BTh,
    BTd,
  },
  setup() {
    const data = ref([])
    const headers = ref([])

    onMounted(() => {
      fetchData()
    })

    const toast = useToast()
    const pushToast = (variant, title) => {
      let icon

      if (variant === 'success') {
        icon = 'CheckCircleIcon'
      } else {
        icon = 'AlertTriangleIcon'
      }

      toast({
        component: ToastificationContent,
        props: { title, icon, variant },
      })
    }

    const query = ref({
      startDate: dayjs().add(-7, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().add(-1, 'day').format('YYYY-MM-DD'),
      isPrefixSum: false,
    })

    const changePrefixSum = () => {
      if (query.value.isPrefixSum) {
        query.value.startDate = dayjs().add(-1, 'month').format('YYYY-MM-DD')
      } else {
        query.value.startDate = dayjs().add(-7, 'day').format('YYYY-MM-DD')
      }

      query.value.endDate = dayjs().add(-1, 'day').format('YYYY-MM-DD')
    }

    const fetchData = () => {
      const frDate = dayjs(query.value.startDate)
      const toDate = dayjs(query.value.endDate)

      if (query.value.isPrefixSum && Math.abs(frDate.diff(toDate, 'day')) > 365) {
        pushToast('warning', '조회 기간은 최대 1년을 넘을 수 없습니다.')
        return
      }
      if (query.value.isPrefixSum === false && Math.abs(frDate.diff(toDate, 'day')) > 30) {
        pushToast('warning', '조회 기간은 최대 30일을 넘을 수 없습니다.')
        return
      }

      axios.get('/fa/stats-entry/region', {
        params: {
          startDate: query.value.startDate,
          endDate: query.value.endDate,
          isPrefixSum: query.value.isPrefixSum,
        },
      })
        .then(rs => {
          const result = rs.data

          // 지역 열 목록 (가나다순 - 서버에서 부여한 sortOrder 기준)
          headers.value = _(result).uniqBy('regionCode').map(x => ({
            regionCode: x.regionCode,
            regionName: x.regionName,
            sortOrder: x.sortOrder,
          }))
            .orderBy(['sortOrder'], ['asc'])
            .value()

          // 일자별 행 구성
          data.value = _(result).groupBy('date').map((item, key) => ({
            date: key,
            regions: _(item).orderBy(['sortOrder'], ['asc']).value(),
          })).value()
        })
        .catch(() => {
          pushToast('danger', '데이터 조회 중 오류가 발생하였습니다.')
        })
    }

    const excelDownload = async () => {
      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet("sheet1")

      const regions = headers.value

      const columns = [
        { key: "date", width: 20 },
      ]

      for (let i = 0; i < regions.length; i += 1) {
        columns.push({ key: `total-${regions[i].regionCode}`, width: 15, style: { numFmt: '#,##0' } })
        columns.push({ key: `group-${regions[i].regionCode}`, width: 15, style: { numFmt: '#,##0' } })
      }

      worksheet.columns = columns

      // 헤더 2줄 (지역명 / 유입 수·유입자 수)
      worksheet.spliceRows(1, 0, [], [])

      worksheet.getCell('A1').value = '구분'
      worksheet.mergeCells('A1:A2')

      for (let i = 0; i < regions.length; i += 1) {
        const startCol = 2 + (i * 2)
        worksheet.getRow(1).getCell(startCol).value = regions[i].regionName
        worksheet.mergeCells(1, startCol, 1, startCol + 1)
        worksheet.getRow(2).getCell(startCol).value = '유입 수'
        worksheet.getRow(2).getCell(startCol + 1).value = '유입자 수'
      }

      // eslint-disable-next-line array-callback-return
      data.value.map(e => {
        const record = {
          date: e.date,
        }

        for (let i = 0; i < e.regions.length; i += 1) {
          record[`total-${e.regions[i].regionCode}`] = e.regions[i].entryTotalCount
          record[`group-${e.regions[i].regionCode}`] = e.regions[i].entryGroupCount
        }

        worksheet.addRow(record)
      })

      worksheet.eachRow({ includeEmpty: true }, (row, number) => {
        row.eachCell({ includeEmpty: true }, cell => {
          if (number === 1 || number === 2) {
            // eslint-disable-next-line no-param-reassign
            cell.font = {
              bold: true,
            }

            // eslint-disable-next-line no-param-reassign
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'D3D3D3' },
            }
          }

          // eslint-disable-next-line no-param-reassign
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' },
          }

          // eslint-disable-next-line no-param-reassign
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'left',
          }
        })
      })

      const mimeType = {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], mimeType)

      saveAs(blob, "지역별_유입현황.xlsx")
    }

    return {
      data,
      headers,
      query,
      fetchData,
      changePrefixSum,
      excelDownload,
    }
  },
}
</script>

<style scoped>
.table-bordered th, td {
  border: 1px solid #dee2e6;
}

.table th {
  vertical-align: middle;
}
</style>
